import { SynchTripExportDTO, SynchTripResultExportDTO } from 'business/models/Models';
import SynchTripService from 'business/services/SynchrotronTripService';
import { RMCustomButtonProps } from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import { handleTableFilter } from 'components/main/userGroup/sections/baseGroup';
import Logo from 'Icons/RMlogo.png';
import {
  downloadFilefromUrl,
  getDateISOFormat,
  getFileURL,
  headerButtonsColor,
  headerIconSize,
} from 'utils/Global';
import {
  CrystalDataDTO,
  SynchTripDataDTO,
  SynchTripStatus,
  SynchrotronTripResultLookup,
} from './constants';

const synchTripService = new SynchTripService();

export type GetSynchrotronTripHeaderButtonsParams = {
  isDisabled: boolean;
  getSynchTripReport: () => void;
  importSynchTrip: () => void;
  exportSynchTrip: () => void;
};

export type GetSynchTripInputFieldsParams = {
  synchTrip: SynchTripDataDTO;
  handleInputChange: (e: any) => void;
};

export type TableColumns = {
  accessor?: string;
  Header?: string;
  Filter?: (e: any) => void;
  className?: string;
  Cell?: any;
};

export type TableData = {
  id: number;
  crystalName: string;
  harvestFrom: string;
  proteinName: string;
  wellDrop: string;
  owner: string;
  collected: JSX.Element;
  open?: JSX.Element;
};

export type UnAssignedCrystalsTableData = {
  id: number;
  crystalName: string;
  harvestFrom: string;
  owner: string;
};

export type SynchTripInputs = {
  name: string;
  type: string;
  label?: string;
  value?: any;
  isDisabled?: boolean;
  onChange?: (e: any) => void;
};

export const csvColumnHeadings = {
  status: 'Status',
  synchTripId: 'Synchrotron Trip ID',
  synchTripName: 'Synchrotron Trip Name',
  dataCollectionDate: 'Synchrotron Trip Date',
  crystalId: 'Crystal ID',
  crystalName: 'Crystal Name (RM)',
  puckName: 'Puck Name',
  puckPinPosition: 'Puck Position',
  dewarId: 'Dewar ID',
  dewarName: 'Dewar Name',
};

export const getSynchrotronTripHeaderButtons = (
  params: GetSynchrotronTripHeaderButtonsParams,
) => {
  const { isDisabled, getSynchTripReport, importSynchTrip, exportSynchTrip } = params;

  const synchrotronTripButtons: Array<RMCustomButtonProps> = [
    {
      iconProps: {
        subfolder: 'Reports',
        name: 'Report',
        fill: headerButtonsColor,
        ...headerIconSize,
      },
      title: 'Report',
      isDisabled: isDisabled,
      color: headerButtonsColor,
      contentOrientation: 'Vertical',
      contentContainerClass: 'header-button-container',
      titleClass: 'header-button-title',
      onClick: getSynchTripReport,
    },
    {
      iconProps: {
        name: 'Import',
        fill: headerButtonsColor,
        ...headerIconSize,
      },
      title: 'Import',
      isDisabled: isDisabled,
      contentOrientation: 'Vertical',
      contentContainerClass: 'header-button-container',
      titleClass: 'header-button-title',
      onClick: importSynchTrip,
    },
    {
      iconProps: {
        name: 'Export',
        fill: headerButtonsColor,
        ...headerIconSize,
      },
      title: 'Export',
      isDisabled: isDisabled,
      contentOrientation: 'Vertical',
      contentContainerClass: 'header-button-container',
      titleClass: 'header-button-title',
      onClick: exportSynchTrip,
    },
  ];
  return synchrotronTripButtons;
};

export const tableColumns: TableColumns[] = [
  {
    accessor: 'id',
    Header: 'ID',
    Filter: handleTableFilter('ID'),
    className: 'truncate synch_id',
  },
  {
    accessor: 'crystalName',
    Header: 'Crystal Name',
    Filter: handleTableFilter('Crystal Name'),
    className: 'truncate synch_name',
  },
  {
    accessor: 'harvestFrom',
    Header: 'Harvested From',
    Filter: handleTableFilter('Harvested From'),
    className: 'truncate',
  },
  {
    accessor: 'proteinName',
    Header: 'Protein Name',
    Filter: handleTableFilter('Protein Name'),
    className: 'truncate',
  },
  {
    accessor: 'wellDrop',
    Header: 'Well/Drop',
    Filter: handleTableFilter('Well/Drop'),
    className: 'truncate synch_welldrop',
  },
  {
    accessor: 'owner',
    Header: 'Owner',
    Filter: handleTableFilter('Owner'),
    className: 'truncate',
  },
  {
    accessor: 'collected',
    Header: 'Collected',
    Filter: handleTableFilter('Collected'),
    className: 'synch_collected',
  },
  {
    accessor: 'open',
    className: 'synch_open',
  },
];

export const unAssignedCrystalsTableColumns: TableColumns[] = [
  {
    accessor: 'id',
    Header: 'ID',
    Filter: handleTableFilter('ID'),
    className: 'truncate synch_id',
  },
  {
    accessor: 'crystalName',
    Header: 'Crystal Name',
    Filter: handleTableFilter('Crystal Name'),
    className: 'truncate',
  },
  {
    accessor: 'harvestFrom',
    Header: 'Harvested From',
    Filter: handleTableFilter('Harvested From'),
    className: 'truncate',
  },
  {
    accessor: 'owner',
    Header: 'Owner',
    Filter: handleTableFilter('Owner'),
    className: 'truncate',
  },
];

export const getSynchTripInputFields = (params: GetSynchTripInputFieldsParams) => {
  const { synchTrip, handleInputChange } = params;
  return [
    {
      label: 'Status',
      type: 'text',
      name: 'status',
      value: getSynchTripStatus(synchTrip?.status),
      onChange: handleInputChange,
      isDisabled: true,
    },
    {
      label: 'ID',
      type: 'text',
      name: 'id',
      value: synchTrip?.id,
      isDisabled: true,
    },
    {
      label: 'Synchrotron Trip Name',
      type: 'text',
      name: 'synchTripName',
      value: synchTrip?.synchTripName,
      onChange: handleInputChange,
    },
    {
      label: 'Synchrotron Trip Date',
      type: 'date',
      name: 'dataCollectionDate',
      value: getDateISOFormat(synchTrip?.dataCollectionDate),
      onChange: handleInputChange,
    },
    {
      label: 'Beamline',
      type: 'text',
      name: 'beamLine',
      value: synchTrip?.beamLine,
      onChange: handleInputChange,
    },
    {
      label: 'Phone Number',
      type: 'text',
      name: 'phoneNumber',
      value: synchTrip?.phoneNumber,
      onChange: handleInputChange,
    },
    {
      label: 'Detector Type',
      type: 'text',
      name: 'detectorType',
      value: synchTrip?.detectorType,
      onChange: handleInputChange,
    },
    {
      label: 'Contact Person',
      type: 'text',
      name: 'contactPerson',
      value: synchTrip?.contactPerson,
      onChange: handleInputChange,
    },
  ];
};

export const getReportHeader = () => {
  return [
    {
      columns: [
        {
          image: Logo,
          width: 20,
          height: 20,
        },
        {
          text: 'ROCK MAKER®',
          margin: [5, 4, 0, 0],
          width: 440,
          fontSize: 12,
          color: '#707070',
        },
        {
          text: 'Printed On: ' + new Date().toLocaleDateString('en-US'),
          margin: [0, 5, 0, 0],
          color: '#707070',
        },
      ],
      margin: [20, 20, 20, 20],
    },
  ];
};

export const createSynchTrip = async (parentTreeNode?: number) => {
  const synchTripService = new SynchTripService();
  const parentTreeNodeId = await synchTripService.getSynchTripsRootTreeNodeId();
  const newSynchTrip = parentTreeNodeId
    ? await synchTripService.createSynchTrip(parentTreeNode || parentTreeNodeId)
    : null;
  return { newSynchTrip, parentTreeNodeId };
};

export const exportSynchTripCSV = async (
  synchTripExportDto: SynchTripExportDTO,
  name: string,
) => {
  const res = await synchTripService.getSynchTripExportCSV(synchTripExportDto);
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const link = document.createElement('a');
  link.href = url;
  const date = new Date();
  link.setAttribute(
    'download',
    `${name}_${date.getFullYear()}_${
      date.getMonth() + 1
    }_${date.getDate()}_${date.getHours()}_${date.getMinutes()}_${date.getSeconds()}.csv`,
  );
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const exportSynchTripResults = async (
  synchTripResultsDto: SynchTripResultExportDTO[],
  tripName,
) => {
  const res = await synchTripService.getSynchTripResultsExportCSV(synchTripResultsDto);
  const url = getFileURL(res, 'text/csv;charset=utf-8;');
  downloadFilefromUrl(url, `${tripName}.csv`);
};

export const mapSynchTripResultData = (crystalResults, selectedCrystals, tripInfo) => {
  const updatedCrystalResults: SynchTripResultExportDTO[] = crystalResults
    .filter((result) => selectedCrystals.includes(result.id))
    .map((crystal) => {
      const { result, ...rest } = crystal;
      return {
        ...rest,
        ...result,
        resultStatus: SynchrotronTripResultLookup[result?.resultStatus] || 'No Result',
        puckPinPostion: crystal.position,
        crystalId: crystal.id,
        synchTripId: tripInfo?.tripId,
        synchTripName: tripInfo?.tripName,
      };
    });
  return updatedCrystalResults;
};

export const getFilteredCrystalsAfterUnAssignment = (
  crystals: CrystalDataDTO[],
  crystalsAfterUnAssignment: CrystalDataDTO[],
) =>
  crystals.filter((obj) => crystalsAfterUnAssignment.some((item) => item.id === obj.id));

export const getSynchTripStatus = (status: number) => {
  return status > 0 ? SynchTripStatus.Open : SynchTripStatus.Close;
};

export const reportOptionsColumns: TableColumns[] = [
  {
    accessor: 'synchTripName',
    Header: 'Name',
    className: 'truncate',
  },
];
